import VZRangeSlider from './VZRangeSlider';
import * as debounce from 'lodash/debounce';
import { getUrlFromAttributes, appendParamToURL, getParamFromURL, appendBmHardwareCardIndex } from 'Util/urlUtils';

/**
 * @function calculateOneTimePrice
 * @param {number} netPrice - net price
 * @param {number} monthlyCost - monthly cost
 * @param {number} contractDuration - contract duration
 * @description calculates one time cost for selected monthly price
 * @returns {number} - one time cost
 */
function calculateOneTimePrice(netPrice, monthlyCost, contractDuration) {
    const maxMonthlyCost = Math.min(40, Math.floor(netPrice / (contractDuration + 1)));
    if (monthlyCost > maxMonthlyCost) {
        return null;
    }
    return (netPrice - contractDuration * monthlyCost).toFixed(2);
}

export default class VZPriceBalanceSlider extends VZRangeSlider {
    constructor() {
        super();
    }

    /**
     * @function observedAttributes
     * @description observes the mentioned attribute and fires attributeChangedCallback when the value has changed
     * @returns {string[]} Array of attributes to observe
     */
    static get observedAttributes() {
        return ['recurring', 'one-off'];
    }

    /**
     * @function attributeChangedCallback
     * @description fired when an observed attribute changes
     * @param {string} name the name of the attribute
     * @param {any} oldValue the previous value of the attribute
     * @param {any} newValue the new value of the attribute
     */
    attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue === newValue) return;

        if (name === 'recurring') {
            this.recurringPrice = newValue;
        }
        if (name === 'one-off') {
            this.oneOffPrice = newValue;
        }
    }

    /**
     * @function initializeElements
     * @description Initialize all DOM elements and component properties
     */
    initializeElements() {
        super.initializeElements();

        this.oneOffPrice = this.getAttribute('one-off');
        this.netPrice = this.getAttribute('net-price');
        this.recurringPrice = this.getAttribute('recurring');
        this.contractDuration = this.getAttribute('contract-duration');
        this.priceContainer = this.querySelector('.js-range-slider-select-price');
        this.recurringPriceMsg = this.querySelector('.js-hardware-recurring');
        this.oneOffPriceMsg = this.querySelector('.js-hardware-one-off');
        this.hardwareWrapper = this.closest('.js-hardware-phones');
        this.cardIndex = this.hardwareWrapper?.dataset.index;
        this.editMode = document.querySelector('.js-edit-mode');

        this.debouncedChangeEvent = debounce(this.dispatchChange.bind(this), 1000);
    }

    /**
     * @function onInputChange
     * @description Handle input changes
     * @param {Event} evt - The input event
     */
    onInputChange(evt) {
        super.onInputChange(evt);

        this.updateUI(evt.target.value);
        this.debouncedChangeEvent();
    }

    /**
     * @function updatePriceDisplay
     * @description Update the price display with new values
     * @param {number} recurringPrice - The new recurring price
     */
    updateUI(recurringPrice) {
        const oneTimeCost = calculateOneTimePrice(Number(this.netPrice), Number(recurringPrice), Number(this.contractDuration));

        this.setAttribute('one-off', oneTimeCost);
        this.setAttribute('recurring', recurringPrice);

        super.updatePriceDisplay({
            recurringPrice,
            oneOffPrice: oneTimeCost
        });
    }

    /**
     * @function dispatchChange
     * @description Dispatch change event and update product configuration
     */
    async dispatchChange() {
        const detail = {
            identifier: this.cardIndex,
            oneOffPrice: Number.parseFloat(this.oneOffPrice),
            recurringPrice: Number.parseInt(this.recurringPrice, 10),
            contractDuration: Number.parseInt(this.contractDuration, 10)
        };

        // Dispatch price balance update event
        const updateEvent = new CustomEvent('vzpricebalance:update', {
            bubbles: true,
            detail
        });
        this.dispatchEvent(updateEvent);

        // Handle product card update if needed
        const VZProductCard = this.closest('vz-product-card');
        const urlAttribute = this.hardwareWrapper?.querySelector('input[name^="hardware_storageCapacity"]:checked')?.getAttribute('data-url');

        if (!urlAttribute || !VZProductCard) return;

        this.spinner.start();
        try {
            const url = this.buildRequestUrl(urlAttribute, VZProductCard, detail);
            const response = await fetch(url);
            const responseText = await response.text();

            VZProductCard.dispatchEvent(
                new CustomEvent('variationChange', {
                    detail: { response: responseText }
                })
            );
        } catch (error) {
            console.error('Error updating product configuration:', error);
        } finally {
            this.spinner.stop();
        }
    }

    /**
     * @function buildRequestUrl
     * @description Builds the request URL with all necessary parameters
     * @param {string} urlAttribute - Base URL from the storage capacity input
     * @param {HTMLElement} productCard - The product card element
     * @param {Object} priceDetails - Price details to update
     * @returns {string} Complete URL with all parameters
     * @private
     */
    buildRequestUrl(urlAttribute, productCard, priceDetails) {
        const url = getUrlFromAttributes(urlAttribute, productCard, ['class', 'data-configurator-type', 'data-url', 'data-general-error']);

        // Handle business rules products
        let businessRulesProducts = productCard.getAttribute('businessrules-products');
        if (businessRulesProducts) {
            businessRulesProducts = JSON.parse(businessRulesProducts);
            Object.keys(businessRulesProducts).forEach((key) => {
                const deviceIndex = key && key.split('---device')[1];
                if (businessRulesProducts[key].isMobileHardware && this.cardIndex === deviceIndex) {
                    Object.assign(businessRulesProducts[key], {
                        oneOffPrice: priceDetails.oneOffPrice,
                        recurringPrice: priceDetails.recurringPrice,
                        contractDuration: priceDetails.contractDuration
                    });
                }
            });

            let finalUrl = appendBmHardwareCardIndex(
                this,
                appendParamToURL(
                    appendParamToURL(url, 'businessrulesproducts', JSON.stringify(businessRulesProducts)),
                    'reversed',
                    productCard?.getAttribute('data-reversed') === 'true'
                )
            );

            if (this.editMode) {
                const pliId = getParamFromURL(window.location.href, 'pliId');
                if (pliId) {
                    finalUrl = appendParamToURL(finalUrl, 'pliId', pliId);
                }
            }

            return finalUrl;
        }

        let finalUrl = appendBmHardwareCardIndex(this, appendParamToURL(url, 'reversed', productCard?.getAttribute('data-reversed') === 'true'));

        if (this.editMode) {
            const pliId = getParamFromURL(window.location.href, 'pliId');
            if (pliId) {
                finalUrl = appendParamToURL(finalUrl, 'pliId', pliId);
            }
        }

        return finalUrl;
    }
}

if (!window.customElements.get('vz-price-balance-slider')) {
    window.customElements.define('vz-price-balance-slider', VZPriceBalanceSlider);
}
